import { Rocket, X } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { HealthPassCardWidget } from "../../components/card/HealthPassCardWidget";
import { NextBestStepCard } from "../../components/home/NextBestStepCard";
import { NextTripPass } from "../../components/home/NextTripPass";
import { PassportSection } from "../../components/home/PassportSection";
import {
  VitalsGlance,
  type VitalTile,
} from "../../components/home/VitalsGlance";
import {
  bloodPressureStatus,
  bmiStatus,
  dismissKey,
  isStale,
  nextBestStep,
  pulseStatus,
  readingAge,
  type NextStep,
} from "../../components/home/homeLogic";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import { useHealthPassCard } from "../../hooks/useHealthPassCard";
import {
  getPassport,
  getReadinessSummaries,
  type Passport,
  type ReadinessSummary,
} from "../../services/api/travelHealth";
import { getTravels, type Travel } from "../../services/api/travels";
import {
  bodyDataService,
  type BodyDataHistoryItem,
} from "../../services/bodyData.service";
import { healthDataService } from "../../services/healthData.service";
import { useAuthStore } from "../../stores/authStore";
import { useI18nStore } from "../../stores/i18nStore";
import type { HealthDataForm } from "../../types/fhir-health";
import { log } from "@/lib/log";

/**
 * Home (UX_CONCEPT.md §5.1): greeting, the next trip as a boarding pass, the
 * card, one next best step, the readings at a glance, and the passport.
 *
 * This replaces a 1,750-line dashboard that showed a hardcoded "Health Status:
 * Optimal", made-up activity and reminder lists, static wellness tips, and
 * quick actions, affiliate tiles and a PLUS promo whose buttons did nothing.
 * Every number on this page now comes from the traveller's own data.
 */

const DISMISSED_KEY = "healthpass.home.dismissed";
const BETA_DISMISSED_KEY = "healthpass.home.betaDismissed";

function readDismissed(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function writeDismissed(keys: Set<string>): void {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...keys]));
  } catch {
    // Not remembered across visits; dismissed for this visit all the same.
  }
}

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeFlag(key: string): void {
  try {
    localStorage.setItem(key, "1");
  } catch {
    // See writeDismissed.
  }
}

function upcomingTrips(travels: Travel[], now: Date): Travel[] {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return travels
    .filter(
      (trip) =>
        trip.departureDate &&
        new Date(trip.departureDate) >= today &&
        ["planned", "current", "draft"].includes(trip.status),
    )
    .sort(
      (a, b) =>
        new Date(a.departureDate ?? "").getTime() -
        new Date(b.departureDate ?? "").getTime(),
    );
}

/** The newest entry, whatever order the API returns them in. */
function latestBodyData(
  history: BodyDataHistoryItem[],
): BodyDataHistoryItem | null {
  return history.reduce<BodyDataHistoryItem | null>(
    (latest, item) =>
      !latest ||
      new Date(item.timestamp).getTime() > new Date(latest.timestamp).getTime()
        ? item
        : latest,
    null,
  );
}

export const DashboardPage: React.FC = () => {
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const { user, token } = useAuthStore();
  const { cardData, isLoading: cardLoading } = useHealthPassCard();
  const now = useMemo(() => new Date(), []);

  const [healthData, setHealthData] = useState<HealthDataForm | null>(null);
  const [bodyHistory, setBodyHistory] = useState<BodyDataHistoryItem[]>([]);
  const [vitalsLoading, setVitalsLoading] = useState(true);
  const [vitalsFailed, setVitalsFailed] = useState(false);
  const [reload, setReload] = useState(0);

  const [trips, setTrips] = useState<Travel[]>([]);
  const [readiness, setReadiness] = useState<Record<string, ReadinessSummary>>(
    {},
  );
  const [tripsLoading, setTripsLoading] = useState(true);
  const [passport, setPassport] = useState<Passport | null>(null);
  const [passportLoading, setPassportLoading] = useState(true);

  const [dismissed, setDismissed] = useState<Set<string>>(readDismissed);
  const [betaDismissed, setBetaDismissed] = useState(() =>
    readFlag(BETA_DISMISSED_KEY),
  );

  useEffect(() => {
    if (!user?.id || !token) return;
    let cancelled = false;
    setVitalsLoading(true);
    setVitalsFailed(false);

    (async () => {
      try {
        const data = await healthDataService.getHealthData(user.id, token);
        let history: BodyDataHistoryItem[] = [];
        try {
          history = await bodyDataService.getBodyDataHistory(12);
        } catch (error) {
          log.debug("No body data history available:", error);
        }
        if (!cancelled) {
          setHealthData(data);
          setBodyHistory(history);
        }
      } catch (error) {
        console.error("Home: health data could not be loaded:", error);
        if (!cancelled) setVitalsFailed(true);
      } finally {
        if (!cancelled) setVitalsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, token, reload]);

  useEffect(() => {
    if (!user?.id || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const [travels, summaries] = await Promise.all([
          getTravels(),
          getReadinessSummaries().catch(() => ({})),
        ]);
        if (!cancelled) {
          setTrips(upcomingTrips(travels, now));
          setReadiness(summaries);
        }
      } catch (error) {
        console.error("Home: trips could not be loaded:", error);
      } finally {
        if (!cancelled) setTripsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, token, now]);

  useEffect(() => {
    if (!user?.id || !token) return;
    let cancelled = false;
    getPassport()
      .then((data) => {
        if (!cancelled) setPassport(data);
      })
      .catch((error) => {
        console.error("Home: passport could not be loaded:", error);
      })
      .finally(() => {
        if (!cancelled) setPassportLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, token]);

  const tiles = useMemo<VitalTile[]>(() => {
    const result: VitalTile[] = [];
    const bp = healthData?.bloodPressure;
    if (bp?.systolic && bp?.diastolic) {
      result.push({
        key: "bp",
        label: t.bloodPressure,
        value: `${bp.systolic}/${bp.diastolic}`,
        unit: "mmHg",
        status: bloodPressureStatus(bp.systolic, bp.diastolic),
        age: readingAge(bp.recordedAt, now),
        stale: isStale(bp.recordedAt, now),
      });
    }
    const hr = healthData?.restingHeartRate;
    if (hr?.value) {
      result.push({
        key: "pulse",
        label: t.pulse,
        value: String(hr.value),
        unit: "bpm",
        status: pulseStatus(hr.value),
        age: readingAge(hr.recordedAt, now),
        stale: isStale(hr.recordedAt, now),
      });
    }
    // The body-data history carries dates; the profile's weight does not, and
    // is then shown as "date unknown" rather than as today's.
    const latest = latestBodyData(bodyHistory);
    const weight = latest?.weight ?? healthData?.weight?.value;
    const weightAt = latest?.timestamp ?? null;
    if (weight) {
      result.push({
        key: "weight",
        label: t.weightLabel,
        // Stored in kg; shown in lb for English.
        value: fmt.number(fmt.toDisplay("weight", Number(weight))),
        unit: fmt.unit("weight"),
        status: null,
        age: readingAge(weightAt, now),
        stale: isStale(weightAt, now),
      });
    }
    const bmi = latest?.bmi ?? healthData?.bmi;
    if (bmi) {
      result.push({
        key: "bmi",
        label: t.bmiLabel,
        value: fmt.number(bmi, 1),
        unit: "",
        status: bmiStatus(bmi),
        age: readingAge(weightAt, now),
        stale: isStale(weightAt, now),
      });
    }
    return result;
  }, [healthData, bodyHistory, now, t, fmt]);

  const nextTrip = trips[0] ?? null;
  const nextTripReadiness = nextTrip ? (readiness[nextTrip._id] ?? null) : null;
  const nextTripCity = nextTrip
    ? nextTrip.destination || nextTrip.country || nextTrip.tripTitle || ""
    : "";

  // Decided only once everything it depends on has arrived, so the page never
  // shows one nudge and swaps it for another a second later.
  const step: NextStep | null =
    cardLoading || vitalsLoading || tripsLoading
      ? null
      : nextBestStep({
          bloodType: cardData?.emergencySummary?.bloodType ?? null,
          bloodPressure: healthData?.bloodPressure ?? null,
          nextTrip: nextTrip
            ? {
                id: nextTrip._id,
                destination: nextTripCity,
                readinessPercent: nextTripReadiness?.percent ?? null,
              }
            : null,
          dismissed,
          now,
        });

  const dismissStep = (current: NextStep) => {
    const next = new Set(dismissed);
    next.add(dismissKey(current));
    setDismissed(next);
    writeDismissed(next);
  };

  const hour = now.getHours();
  const greeting =
    hour < 12
      ? t.homeGoodMorning
      : hour < 18
        ? t.homeGoodAfternoon
        : t.homeGoodEvening;

  return (
    <div className="mx-auto max-w-6xl">
      <header>
        <p className="text-sm text-muted-foreground">{greeting}</p>
        <h1 className="font-display text-[28px] font-extrabold leading-tight text-ink sm:text-4xl">
          {user?.firstName || ""}
        </h1>
      </header>

      {!betaDismissed && (
        <div className="mt-4 flex items-start gap-3 rounded-2xl bg-white px-4 py-3 text-sm text-muted-foreground">
          <Rocket
            className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-600"
            aria-hidden="true"
          />
          <span className="flex-1">{t.homeBetaNotice}</span>
          <button
            type="button"
            onClick={() => {
              setBetaDismissed(true);
              writeFlag(BETA_DISMISSED_KEY);
            }}
            aria-label={t.homeDismiss}
            className="-m-2 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl hover:bg-muted"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}

      <div className="mt-6 grid items-start gap-7 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div className="lg:col-start-1">
          {tripsLoading ? (
            <div className="h-[210px] rounded-[22px] bg-white animate-pulse" />
          ) : (
            <NextTripPass
              trip={nextTrip}
              moreCount={Math.max(trips.length - 1, 0)}
              readiness={nextTripReadiness}
              now={now}
            />
          )}
        </div>

        <div className="lg:col-start-2 lg:row-span-3 lg:row-start-1 lg:rounded-[22px] lg:bg-white lg:p-5">
          <HealthPassCardWidget />
        </div>

        {step && (
          <div className="lg:col-start-1">
            <NextBestStepCard
              step={step}
              onDismiss={() => dismissStep(step)}
              now={now}
            />
          </div>
        )}

        <div className="lg:col-start-1">
          <VitalsGlance
            tiles={tiles}
            loading={vitalsLoading}
            failed={vitalsFailed}
            onRetry={() => setReload((n) => n + 1)}
          />
        </div>

        <div className="lg:col-start-1">
          <PassportSection
            passport={passport}
            loading={passportLoading}
            failed={!passportLoading && !passport}
            nextTrip={
              nextTrip && nextTripCity
                ? { id: nextTrip._id, city: nextTripCity }
                : null
            }
          />
        </div>
      </div>
    </div>
  );
};
